import NoiseFetchLayer from './NoiseFetchLayer';
import { decibelRangeLabel, decibelsLabel, soundLevelLabel } from '../utils/decibels';
import { expandAddressAbbreviations } from '../utils/address-abbreviations';
import { pluralPl } from '../../shared/polish-plural';

function escapeHtml(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
}

function safeUrl(value) {
    try {
        const url = new URL(String(value || ''));
        return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : '';
    } catch (_error) {
        return '';
    }
}

function indicatorLabel(value) {
    const labels = {
        LDWN: 'Długookresowy poziom hałasu dla dnia, wieczoru i nocy (LDWN)',
        LD: 'Długookresowy poziom hałasu w porze dnia (LD)',
        LN: 'Długookresowy poziom hałasu w nocy (LN)',
        LAEQD: 'Równoważny poziom hałasu w dzień (LAeqD)',
        LAEQE: 'Równoważny poziom hałasu wieczorem (LAeqE)',
        LAEQN: 'Równoważny poziom hałasu w nocy (LAeqN)'
    };
    const key = String(value || '').replace(/[^a-z0-9]/gi, '').toUpperCase();
    return labels[key] || String(value || 'Nie podano wskaźnika');
}

/**
 * The reference time of a measurement in words: „Dzień 16h” → „dzień, 16 godzin”.
 * Twin of NoiseBlocks::referenceTime() of the parcel report.
 */
export function referenceTimeLabel(value) {
    let text = String(value == null ? '' : value).trim();
    if (!text) return '';
    text = text.replace(/(\d+)\s*(?:h|godz\.?)(?![a-ząćęłńóśźż])/giu, (match, hours) => ', ' + hours + ' '
        + pluralPl(Number(hours), ['godzina', 'godziny', 'godzin']));
    text = text.replace(/\s+,/gu, ',').replace(/^[\s,]+|[\s,]+$/gu, '');
    return text.charAt(0).toLowerCase() + text.slice(1);
}

function noiseTypeLabel(value) {
    return { road: 'Hałas drogowy', rail: 'Hałas kolejowy', air: 'Hałas lotniczy',
        industrial: 'Hałas przemysłowy' }[value] || 'Nie określono rodzaju hałasu';
}

function dateLabel(value) {
    if (!value) return '';
    const date = new Date(value);
    return Number.isFinite(date.getTime()) ? date.toLocaleDateString('pl-PL', { timeZone: 'UTC' }) : String(value);
}

export function measurementHtml(properties, metadata) {
    const p = properties || {};
    const provenance = (metadata && metadata.provenance) || {};
    const raw = p.value;
    const measured = raw !== null && raw !== undefined && raw !== '' && Number.isFinite(Number(raw));
    const level = measured ? soundLevelLabel(raw, p.unit) : null;
    const period = [dateLabel(p.observedFrom || p.observedAt), dateLabel(p.observedTo)]
        .filter(Boolean).join(' – ');
    const source = provenance.sourceName || provenance.provider || 'Główny Inspektorat Ochrony Środowiska';
    const url = safeUrl(provenance.sourceUrl || p.sourceUrl);
    const row = (label, text) => '<div class="nm-layer-info-row"><span>' + escapeHtml(label)
        + '</span><span>' + escapeHtml(text) + '</span></div>';
    if (p.aggregate) {
        const count = Number(p.pointCount || 0);
        const range = p.minValue != null && p.maxValue != null
            && Number.isFinite(Number(p.minValue)) && Number.isFinite(Number(p.maxValue))
            ? decibelRangeLabel(p.minValue, p.maxValue, 1)
            : '';
        return '<div class="nm-noise-popup"><strong>Pomiary w tej części mapy</strong>'
            + (measured ? '<div class="nm-noise-reading nm-noise-reading--' + escapeHtml(p.assessmentKey || 'unknown') + '">'
                + '<b>' + escapeHtml(p.assessmentLabel || 'Wynik orientacyjny') + '</b>'
                + '<span>Mediana: ' + escapeHtml(decibelsLabel(raw)) + '</span></div>' : '')
            + row('Liczba punktów', count)
            + (range ? row('Zakres wyników', range) : '')
            + (p.latestMeasurement ? row('Najnowszy pomiar', dateLabel(p.latestMeasurement)) : '')
            + '<div class="nm-layer-info-copy">To wartość środkowa z najnowszego dostępnego wyniku każdego punktu w tym obszarze. Może łączyć pomiary z różnych pór i lat, dlatego służy do szybkiej orientacji.</div>'
            + '<div class="nm-layer-info-copy">Przybliż mapę, aby zobaczyć pojedyncze punkty i ich wyniki.</div>'
            + '</div>';
    }
    return '<div class="nm-noise-popup"><strong>Punkt monitoringu hałasu</strong>'
        + '<div class="nm-layer-info-copy">' + escapeHtml(noiseTypeLabel(p.noiseType)) + '</div>'
        + (measured ? '<div class="nm-noise-reading nm-noise-reading--' + escapeHtml(p.assessmentKey || 'unknown') + '">'
            + '<b>' + escapeHtml(p.assessmentLabel || 'Wynik pomiaru') + '</b>'
            + '<span>' + escapeHtml(level) + '</span>'
            + (p.assessmentDescription ? '<small>' + escapeHtml(p.assessmentDescription) + '</small>' : '') + '</div>' : '')
        + row('Identyfikator punktu', p.facilityId || 'Brak')
        + (p.pointName ? row('Nazwa punktu', expandAddressAbbreviations(p.pointName)) : '')
        + (p.locality ? row('Miejscowość', p.locality) : '')
        + row('Wskaźnik', indicatorLabel(p.indicator))
        + (measured ? row('Wynik pomiaru', level)
            : '<div class="nm-layer-info-copy">Brak wyniku liczbowego opublikowanego dla tego rekordu. Punkt nie oznacza poziomu 0 decybeli.</div>')
        + (p.referenceTime ? row('Czas odniesienia', referenceTimeLabel(p.referenceTime)) : '')
        + (period ? row('Okres pomiaru', period) : '')
        + '<div class="nm-layer-info-copy">Pomiar dotyczy wskazanego miejsca i okresu. Nie jest prognozą poziomu hałasu na sąsiednich działkach.</div>'
        + '<div class="nm-layer-info-copy">Źródło: ' + escapeHtml(source) + '</div>'
        + (url ? '<div class="nm-external-links"><a class="ms-link nm-ext-link" href="' + escapeHtml(url)
            + '" target="_blank" rel="noopener noreferrer">Dane źródłowe Głównego Inspektoratu Ochrony Środowiska ↗</a></div>' : '')
        + '</div>';
}

export default class NoiseMonitoringLayer extends NoiseFetchLayer {
    static MIN_ZOOM = 7;

    getSourceId() {
        return 'src-' + this.id;
    }

    getLayerIds() {
        return ['lyr-' + this.id + '-points', 'lyr-' + this.id + '-labels'];
    }

    buildUrl() {
        const bbox = this.config.getCurrentBbox ? this.config.getCurrentBbox(0.12) : '';
        const map = this.getMap();
        const zoom = map && map.getZoom ? map.getZoom() : NoiseMonitoringLayer.MIN_ZOOM;
        return this.config.baseUrl + 'rcn/noise-api/monitoring?bbox=' + encodeURIComponent(bbox)
            + '&zoom=' + encodeURIComponent(zoom) + '&limit=1200';
    }

    _unbindInteractions() {
        if (this._boundMap) {
            const id = this.getLayerIds()[0];
            this._boundMap.off('mouseenter', id, this._onEnter);
            this._boundMap.off('mouseleave', id, this._onLeave);
            this._boundMap = null;
        }
    }

    _bindInteractions(map) {
        if (this._boundMap === map) return;
        this._unbindInteractions();
        const id = this.getLayerIds()[0];
        this._onEnter = () => { map.getCanvas().style.cursor = 'pointer'; };
        this._onLeave = () => { map.getCanvas().style.cursor = ''; };
        map.on('mouseenter', id, this._onEnter);
        map.on('mouseleave', id, this._onLeave);
        this._boundMap = map;
    }

    _addLayers(map, sourceId) {
        const layerId = this.getLayerIds()[0];
        map.addLayer({
            id: layerId,
            type: 'circle',
            source: sourceId,
            paint: {
                'circle-radius': ['case', ['boolean', ['get', 'aggregate'], false],
                    ['interpolate', ['linear'], ['coalesce', ['get', 'pointCount'], 1], 1, 10, 20, 16, 100, 24],
                    6],
                'circle-color': ['match', ['get', 'assessmentKey'],
                    'low', '#16856b', 'moderate', '#d49a20', 'high', '#e0642b',
                    'very_high', '#c63737', '#64748b'],
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': 2,
                'circle-opacity': 0.92
            }
        });
        map.addLayer({
            id: this.getLayerIds()[1],
            type: 'symbol',
            source: sourceId,
            layout: {
                'text-field': ['get', 'mapLabel'],
                'text-size': ['case', ['boolean', ['get', 'aggregate'], false], 11, 10],
                'text-font': ['Noto Sans Bold'],
                'text-offset': ['case', ['boolean', ['get', 'aggregate'], false], ['literal', [0, 1.6]], ['literal', [0, 1.25]]],
                'text-anchor': 'top',
                'text-allow-overlap': false,
                'text-optional': true
            },
            paint: {
                'text-color': '#25312d',
                'text-halo-color': '#ffffff',
                'text-halo-width': 2
            }
        });
        this._bindInteractions(map);
    }

    renderData(data) {
        this._metadata = (data && data.metadata) || {};
        if (this.config.onMetadata) this.config.onMetadata(this._metadata);
        return this.renderSourceData(data, this._addLayers.bind(this));
    }

    clear() {
        this._unbindInteractions();
        super.clear();
    }
}
